import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { slideDownUp, toggleAnimation } from 'src/app/shared/animations';
import { AlertService } from './service/alerts.service';
import { DashboardService } from './service/dashboard.service';
import { AuthService } from './service/auth/auth.service';

@Component({
    templateUrl: './index.html',
    animations: [toggleAnimation, slideDownUp],
})
export class IndexComponent {
    store: any;
    isLoading = true;

    // Dashboard statistics
    adherentStats: any = {};
    partenaireStats: any = {};
    nouvellesDemandes: any[] = [];
    nouvellesDemandesPartenaire: any[] = [];

    // User data and permissions
    currentUser: any = {};

    // Filter properties - Nouveau système avec flag_today
    selectedYear: number | string | null = null;
    selectedMonth: number | string | null = null;
    flagToday: boolean = false; // Par défaut afficher toutes les données
    availableYears: any[] = [];
    availableMonths: any[] = [];
    selectedPeriodText: string = '';

    constructor(
        public storeData: Store<any>,
        private router: Router,
        private alertService: AlertService,
        private dashboardService: DashboardService,
        private authService: AuthService
    ) {
        this.isLoading = false;
        this.initializeFilterOptions();
    }

    ngOnInit() {
        // First check if user is authenticated before loading any data
        if (!this.authService.isLoggedIn()) {
            this.router.navigate(['/auth/login']);
            return;
        }

        // Get user data only if authenticated
        this.currentUser = this.authService.getUserData();
        
        // Verify we have valid user data
        if (!this.currentUser || !this.currentUser.code_role) {
            this.authService.logout();
            return;
        }

        // Only load dashboard data if user is properly authenticated
        this.loadDashboardData();
    }

    /**
     * Initialize filter options for year and month
     */
    initializeFilterOptions() {
        // Initialize years (last 5 years + current year)
        const currentYear = new Date().getFullYear();
        this.availableYears = [];
        for (let year = currentYear - 5; year <= currentYear; year++) {
            this.availableYears.push({
                label: year.toString(),
                value: year
            });
        }

        // Initialize months in French with short labels for buttons
        this.availableMonths = [
            { label: 'Janvier', shortLabel: 'Jan', value: 1 },
            { label: 'Février', shortLabel: 'Fév', value: 2 },
            { label: 'Mars', shortLabel: 'Mar', value: 3 },
            { label: 'Avril', shortLabel: 'Avr', value: 4 },
            { label: 'Mai', shortLabel: 'Mai', value: 5 },
            { label: 'Juin', shortLabel: 'Jun', value: 6 },
            { label: 'Juillet', shortLabel: 'Jul', value: 7 },
            { label: 'Août', shortLabel: 'Aoû', value: 8 },
            { label: 'Septembre', shortLabel: 'Sep', value: 9 },
            { label: 'Octobre', shortLabel: 'Oct', value: 10 },
            { label: 'Novembre', shortLabel: 'Nov', value: 11 },
            { label: 'Décembre', shortLabel: 'Déc', value: 12 }
        ];

        // Par défaut, afficher toutes les données
        this.flagToday = false;
        this.selectedYear = null;
        this.selectedMonth = null;
        this.updateSelectedPeriodText();
    }

    /**
     * Check if user has access to a specific module/permission
     * @param moduleCode - The module code to check
     */
    hasAccess(moduleCode: string): boolean {
        return this.authService.hasModuleAccess(moduleCode);
    }

    /**
     * Check if user has specific role
     * @param role - Role code to check ('ADMI', 'AGEN', 'UMAS')
     */
    hasRole(role: string): boolean {
        return this.currentUser?.code_role === role;
    }

    /**
     * Check if user has any of the specified roles
     * @param roles - Array of role codes to check
     */
    hasAnyRole(roles: string[]): boolean {
        return roles.includes(this.currentUser?.code_role);
    }

    /**
     * Navigate to specific route when card is clicked (with permission check)
     * @param route - The route path to navigate to
     * @param moduleCode - Optional module code to check permission
     * @param filterByEnCours - If true, filter by "en cours" status and current date
     */
    navigateToRoute(route: string, moduleCode?: string, filterByEnCours: boolean = false) {
        try {
            // If moduleCode is provided, check permission first
            if (moduleCode && !this.hasAccess(moduleCode)) {
                this.alertService.sweetAlertIt('Vous n\'avez pas les permissions nécessaires pour accéder à cette section', 'Accès refusé!', 'warning');
                return;
            }

            // Prepare query params if filterByEnCours is true
            if (filterByEnCours) {
                const queryParams: any = {
                    statut: 'ENCO' // Code statut "En cours"
                };

                // Calculate date range based on current filter selection
                if (this.flagToday) {
                    // Today's date
                    const today = new Date();
                    const todayStr = this.formatDate(today);
                    queryParams.date_debut = todayStr;
                    queryParams.date_fin = todayStr;
                } else {
                    // Calculate date range based on year/month selection
                    const yearNum = typeof this.selectedYear === 'number' ? this.selectedYear : null;
                    const monthNum = typeof this.selectedMonth === 'number' ? this.selectedMonth : null;

                    if (yearNum && monthNum) {
                        // Specific month: first day to last day of the month
                        const firstDay = new Date(yearNum, monthNum - 1, 1);
                        const lastDay = new Date(yearNum, monthNum, 0); // Day 0 of next month = last day of current month
                        queryParams.date_debut = this.formatDate(firstDay);
                        queryParams.date_fin = this.formatDate(lastDay);
                    } else if (yearNum && !monthNum) {
                        // Entire year: January 1st to December 31st
                        const firstDay = new Date(yearNum, 0, 1);
                        const lastDay = new Date(yearNum, 11, 31);
                        queryParams.date_debut = this.formatDate(firstDay);
                        queryParams.date_fin = this.formatDate(lastDay);
                    }
                    // If no year/month selected, don't add date filters (all data)
                }

                this.router.navigate([route], { queryParams });
            } else {
                this.router.navigate([route]);
            }
        } catch (error) {
            console.error('Navigation error:', error);
            this.alertService.sweetAlertIt('Erreur de navigation', 'Erreur!', 'error');
        }
    }

    /**
     * Format date to YYYY-MM-DD format
     * @param date - Date object to format
     * @returns Formatted date string
     */
    private formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Get total count of all adherent requests (only for accessible modules)
     */
    getTotalAdherentRequests(): number {
        let total = 0;
        if (this.hasAccess('FDSA')) total += (this.adherentStats?.feuillesSoins || 0);
        if (this.hasAccess('PECA')) total += (this.adherentStats?.prisesEnCharge || 0);
        if (this.hasAccess('ADHA')) total += (this.adherentStats?.familleAdhesion || 0);
        if (this.hasAccess('ALDA')) total += (this.adherentStats?.affectionsLongueDuree || 0);
        if (this.hasAccess('EPA')) total += (this.adherentStats?.ententesPrealables || 0);
        if (this.hasAccess('RECA')) total += (this.adherentStats?.reclamations || 0);
        return total;
    }


    /**
     * Get total count of all partenaire requests (only for accessible modules)
     */
    getTotalPartenaireRequests(): number {
        let total = 0;
        if (this.hasAccess('PECP')) total += (this.partenaireStats?.prisesEnCharge || 0);
        if (this.hasAccess('INFO')) total += (this.partenaireStats?.informations || 0);
        if (this.hasAccess('RECP')) total += (this.partenaireStats?.reclamations || 0);
        return total;
    }


    /**
     * Check if user should see Adherent section at all
     */
    shouldShowAdherentSection(): boolean {
        return this.hasAccess('FDSA') ||
                this.hasAccess('PECA') ||
                this.hasAccess('ADHA') ||
                this.hasAccess('ALDA') ||
                this.hasAccess('EPA') ||
                this.hasAccess('RECA');
    }


    /**
     * Check if user should see Partenaire section at all
     */
    shouldShowPartenaireSection(): boolean {
        return this.hasAccess('PECP') || 
                this.hasAccess('INFO') || 
                this.hasAccess('RECP');    
    }


    /**
     * Check if user should see Actions Rapides section
     */
    shouldShowActionsRapides(): boolean {
        return this.hasAccess('DSHC') ||
                this.hasAccess('CONF') ||
                this.hasAccess('CONP') ||
                this.hasAccess('UTIL') ||
                this.hasAccess('ADHP') ||
                this.hasAccess('STAT') ||  
                this.hasAccess('PART');
    }


    /**
     * Handle month dropdown change - Updated with better type handling
     */
    onMonthChange() {
        // Désactiver flag_today quand on utilise les filtres manuels
        this.flagToday = false;

        // Traiter la valeur du mois
        if (!this.selectedMonth || this.selectedMonth === '' || this.selectedMonth === 'null') {
            this.selectedMonth = null;
        } else {
            const monthNum = Number(this.selectedMonth);
            if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
                this.selectedMonth = monthNum;
            } else {
                this.selectedMonth = null;
            }
        }

        this.updateSelectedPeriodText();
        this.loadDashboardData();
    }

    /**
     * Handle year dropdown change - Updated with better type handling
     */
    onYearChange() {
        // Désactiver flag_today quand on utilise les filtres manuels
        this.flagToday = false;

        // Traiter la valeur de l'année
        if (!this.selectedYear || this.selectedYear === '' || this.selectedYear === 'null') {
            this.selectedYear = null;
            // Si on efface l'année, effacer aussi le mois (logique API)
            this.selectedMonth = null;
        } else {
            const yearNum = Number(this.selectedYear);
            if (!isNaN(yearNum) && yearNum > 1900 && yearNum < 3000) {
                this.selectedYear = yearNum;
            } else {
                this.selectedYear = null;
                this.selectedMonth = null;
            }
        }

        this.updateSelectedPeriodText();
        this.loadDashboardData();
    }

    /**
     * Update the selected period text for display
     */
    updateSelectedPeriodText() {
        if (this.flagToday) {
            this.selectedPeriodText = 'Aujourd\'hui';
            return;
        }

        const monthNum = typeof this.selectedMonth === 'number' ? this.selectedMonth : null;
        const yearNum = typeof this.selectedYear === 'number' ? this.selectedYear : null;

        if (monthNum && yearNum) {
            const monthLabel = this.availableMonths.find(m => m.value === monthNum)?.label;
            this.selectedPeriodText = `${monthLabel} ${yearNum}`;
        } else if (yearNum && !monthNum) {
            this.selectedPeriodText = `Année ${yearNum}`;
        } else if (!yearNum && !monthNum) {
            this.selectedPeriodText = 'Toutes les données';
        } else {
            this.selectedPeriodText = '';
        }
    }

    /**
     * Get selected period label for the filter summary
     */
    getSelectedPeriodLabel(): string {
        const monthNum = typeof this.selectedMonth === 'number' ? this.selectedMonth : null;
        const yearNum = typeof this.selectedYear === 'number' ? this.selectedYear : null;
        
        if (monthNum && yearNum) {
            const monthLabel = this.availableMonths.find(m => m.value === monthNum)?.label;
            return `${monthLabel} ${yearNum}`;
        } else if (yearNum && !monthNum) {
            return `Année ${yearNum}`;
        }
        return '';
    }

    /**
     * Check if today filter is selected
     */
    isTodaySelected(): boolean {
        return this.flagToday;
    }

    /**
     * Get CSS class for the "Aujourd'hui" button (legacy)
     */
    getTodayButtonClass(): string {
        const baseClasses = 'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all';

        if (this.isTodaySelected()) {
            return `${baseClasses} bg-primary text-white shadow-lg`;
        }

        return `${baseClasses} text-primary hover:text-primary-dark hover:bg-primary/5 dark:hover:bg-primary/10`;
    }

    /**
     * Clear all filters - Toutes les données
     */
    clearFilters() {
        this.flagToday = false;
        this.selectedYear = null;
        this.selectedMonth = null;
        this.updateSelectedPeriodText();
        this.loadDashboardData();
    }

    /**
     * Activer le mode "Aujourd'hui" - Masquer les dropdowns
     */
    setTodayMode() {
        this.flagToday = true;
        this.selectedYear = null;
        this.selectedMonth = null;
        this.updateSelectedPeriodText();
        this.loadDashboardData();
    }

    /**
     * Activer le mode "Filtrer" - Afficher les dropdowns
     */
    setFilterMode() {
        this.flagToday = false;
        // Garder les valeurs actuelles des filtres ou initialiser à null
        this.updateSelectedPeriodText();
        this.loadDashboardData();
    }

    /**
     * Reset filters to today - Réinitialiser tous les contrôles (legacy)
     */
    resetToToday() {
        this.setTodayMode();
    }

    /**
     * Load dashboard statistics from API with current filters
     */
    loadDashboardData() {
        // Double check authentication before making API call
        if (!this.authService.isLoggedIn()) {
            return;
        }

        this.isLoading = true;
        
        // Préparer les paramètres selon les règles API exactes
        const filterParams: any = {};

        if (this.flagToday) {
            // Règle 1: flag_today=true (ignorer year/month)
            filterParams.flag_today = true;
        } else {
            // Règles 2-4: flag_today=false avec year/month
            filterParams.flag_today = false;

            // Convertir et valider year
            let yearNum: number | null = null;
            if (typeof this.selectedYear === 'number') {
                yearNum = this.selectedYear;
            } else if (typeof this.selectedYear === 'string' && this.selectedYear !== '' && this.selectedYear !== 'null') {
                const parsed = Number(this.selectedYear);
                if (!isNaN(parsed)) yearNum = parsed;
            }

            // Convertir et valider month
            let monthNum: number | null = null;
            if (typeof this.selectedMonth === 'number') {
                monthNum = this.selectedMonth;
            } else if (typeof this.selectedMonth === 'string' && this.selectedMonth !== '' && this.selectedMonth !== 'null') {
                const parsed = Number(this.selectedMonth);
                if (!isNaN(parsed)) monthNum = parsed;
            }

            // Appliquer les règles exactes:
            // - Si aucun year/month: year=null, month=null (toutes les données)
            // - Si year mais pas month: year=X, month=null (année seulement)
            // - Si year et month: year=X, month=Y (année et mois)
            filterParams.year = yearNum;
            filterParams.month = monthNum;
        }

        this.dashboardService.getDashboardStats(filterParams).subscribe({
            next: (res: any) => {
                if (res.status === 200) {
                    // Map adherent statistics with "En cours" counts
                    this.adherentStats = {
                        feuillesSoins: res.data.data.adherent_stats.feuilles_soins || 0,
                        feuillesSoinsEnCours: res.data.data.adherent_stats.feuilles_soins_en_cours || 0,
                        prisesEnCharge: res.data.data.adherent_stats.prises_en_charge || 0,
                        prisesEnChargeEnCours: res.data.data.adherent_stats.prises_en_charge_en_cours || 0,
                        familleAdhesion: res.data.data.adherent_stats.famille_adhesion || 0,
                        familleAdhesionEnCours: res.data.data.adherent_stats.famille_adhesion_en_cours || 0,
                        affectionsLongueDuree: res.data.data.adherent_stats.affections_longue_duree || 0,
                        affectionsLongueDureeEnCours: res.data.data.adherent_stats.affections_longue_duree_en_cours || 0,
                        ententesPrealables: res.data.data.adherent_stats.ententes_prealables || 0,
                        ententesPrealablesEnCours: res.data.data.adherent_stats.ententes_prealables_en_cours || 0,
                        reclamations: res.data.data.adherent_stats.reclamations || 0,
                        reclamationsEnCours: res.data.data.adherent_stats.reclamations_en_cours || 0
                    };
                    
                    // Map partenaire statistics with "En cours" counts
                    this.partenaireStats = {
                        prisesEnCharge: res.data.data.part_stats.prises_en_charge || 0,
                        prisesEnChargeEnCours: res.data.data.part_stats.prises_en_charge_en_cours || 0,
                        informations: res.data.data.part_stats.informations || 0,
                        informationsEnCours: res.data.data.part_stats.informations_en_cours || 0,
                        reclamations: res.data.data.part_stats.reclamations || 0,
                        reclamationsEnCours: res.data.data.part_stats.reclamations_en_cours || 0
                    };
                    
                    // Map nouvelles demandes for adherent
                    this.nouvellesDemandes = res.data.data.adherent_stats.nouvelles_demandes?.map((demande: any) => ({
                        code: demande.code_demande || '',
                        type: demande.type_demande || '',
                        nom: demande.nom_adherent || '',
                        date: demande.date_creation || ''
                    })) || [];
                    
                    // Map nouvelles demandes for partenaire
                    this.nouvellesDemandesPartenaire = res.data.data.part_stats.nouvelles_demandes?.map((demande: any) => ({
                        code: demande.code_demande || '',
                        type: demande.type_demande || '',
                        nom: demande.nom_partenaire || '',
                        date: demande.date_creation || ''
                    })) || [];
                } else {
                    // Handle unexpected response status
                    console.warn('Unexpected response status:', res.status);
                    this.initializeEmptyStats();
                }
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error loading dashboard stats:', err);

                // Check if it's an authentication error
                if (err.status === 401) {
                    this.isLoading = false;
                    return;
                }

                // Gestion des nouvelles erreurs de validation
                if (err.status === 400) {
                    const errorMessage = err.error?.message || "Paramètres de filtre invalides";
                    this.alertService.sweetAlertIt(
                        errorMessage,
                        'Paramètres invalides!',
                        'warning'
                    );
                } else {
                    // Autres erreurs
                    this.alertService.sweetAlertIt(
                        "Erreur lors du chargement des statistiques dashboard",
                        'Erreur!',
                        'error'
                    );
                }

                this.initializeEmptyStats();
                this.isLoading = false;
            }
        });
    }

    /**
     * Initialize empty statistics in case of error or no data
     */
    private initializeEmptyStats() {
        this.adherentStats = {
            feuillesSoins: 0,
            feuillesSoinsEnCours: 0,
            prisesEnCharge: 0,
            prisesEnChargeEnCours: 0,
            familleAdhesion: 0,
            familleAdhesionEnCours: 0,
            affectionsLongueDuree: 0,
            affectionsLongueDureeEnCours: 0,
            ententesPrealables: 0,
            ententesPrealablesEnCours: 0,
            reclamations: 0,
            reclamationsEnCours: 0
        };
        
        this.partenaireStats = {
            prisesEnCharge: 0,
            prisesEnChargeEnCours: 0,
            informations: 0,
            informationsEnCours: 0,
            reclamations: 0,
            reclamationsEnCours: 0
        };
        
        this.nouvellesDemandes = [];
        this.nouvellesDemandesPartenaire = [];
    }

    /**
     * Get percentage of adherent requests by type (only for accessible modules)
     * @param type - The type of request
     * @returns percentage as number
     */
    getAdherentPercentage(type: keyof typeof this.adherentStats): number {
        const total = this.getTotalAdherentRequests();
        if (total === 0) return 0;
        return Math.round(((this.adherentStats[type] || 0) / total) * 100);
    }

    /**
     * Get percentage of partenaire requests by type (only for accessible modules)
     * @param type - The type of request
     * @returns percentage as number
     */
    getPartenairePercentage(type: keyof typeof this.partenaireStats): number {
        const total = this.getTotalPartenaireRequests();
        if (total === 0) return 0;
        return Math.round(((this.partenaireStats[type] || 0) / total) * 100);
    }

    /**
     * Refresh dashboard data manually
     */
    refreshDashboard() {
        this.loadDashboardData();
    }

    /**
     * Select a year from the filter buttons (keep for compatibility)
     * @param year - Selected year
     */
    selectYear(year: number) {
        // Désactiver flag_today
        this.flagToday = false;
        this.selectedYear = year;
        // Garder le mois actuel si il existe
        this.updateSelectedPeriodText();
        this.loadDashboardData();
    }

    /**
     * Select a month from the filter buttons (keep for compatibility)
     * @param month - Selected month (1-12)
     */
    selectMonth(month: number) {
        // Désactiver flag_today
        this.flagToday = false;

        if (this.selectedMonth === month) {
            this.selectedMonth = null;
        } else {
            this.selectedMonth = month;
            // S'assurer qu'une année est sélectionnée si on sélectionne un mois
            if (!this.selectedYear) {
                this.selectedYear = new Date().getFullYear();
            }
        }
        this.updateSelectedPeriodText();
        this.loadDashboardData();
    }
}